// Creative Version workflow UI. Buttons are rendered ONLY for the transitions
// the server lists in `allowedTransitions` for the current user; nothing is
// inferred client-side (7.5: no duplicated business rules).
import { Check } from "lucide-react";
import type { VersionState } from "@/api/types";
import { TRANSITION_ACTIONS, VERSION_STATES } from "@/lib/labels";
import { Button } from "@/ui/button";
import { cn } from "@/ui/cn";

const PATH: VersionState[] = ["DRAFT", "PREVIEW", "APPROVED", "PUBLISHED"];

/** Visual progress of the lifecycle (CLIENT_REVIEW shown as part of review). */
export function VersionStepper({ state }: { state: VersionState }) {
  if (state === "ARCHIVED") return <p className="text-sm text-muted">Esta versión está archivada.</p>;
  const position = state === "CLIENT_REVIEW" ? 1 : PATH.indexOf(state);
  return (
    <ol className="flex flex-wrap items-center gap-2 text-xs" aria-label="Progreso de la versión">
      {PATH.map((step, index) => {
        const done = index < position;
        const current = index === position;
        const label =
          step === "PREVIEW" && state === "CLIENT_REVIEW"
            ? VERSION_STATES.CLIENT_REVIEW.label
            : VERSION_STATES[step].label;
        return (
          <li key={step} className="flex items-center gap-2" aria-current={current ? "step" : undefined}>
            <span
              className={cn(
                "flex items-center gap-1 rounded-sm border px-2 py-1",
                current && "border-brand bg-brand-soft font-medium text-brand",
                done && "border-line text-muted",
                !done && !current && "border-dashed border-line text-subtle"
              )}
            >
              {done ? <Check className="size-3" aria-hidden="true" /> : null}
              {label}
            </span>
            {index < PATH.length - 1 ? <span aria-hidden="true" className="h-px w-4 bg-line-strong" /> : null}
          </li>
        );
      })}
    </ol>
  );
}

const ORDER: Array<Exclude<VersionState, "DRAFT">> = ["PREVIEW", "CLIENT_REVIEW", "APPROVED", "PUBLISHED", "ARCHIVED"];

/** One button per transition the server allows; PUBLISHED delegates to the publish dialog. */
export function TransitionActions({
  allowed,
  pending,
  onTransition,
  onPublish,
}: {
  allowed: readonly VersionState[];
  pending: boolean;
  onTransition: (to: Exclude<VersionState, "DRAFT" | "PUBLISHED">) => void;
  onPublish: () => void;
}) {
  const actions = ORDER.filter((to) => allowed.includes(to));
  if (actions.length === 0) return null;
  const primary = actions.find((a) => a !== "ARCHIVED");
  return (
    <div className="flex flex-wrap gap-2">
      {actions.map((to) => (
        <Button
          key={to}
          variant={to === primary ? "primary" : to === "ARCHIVED" ? "danger-outline" : "secondary"}
          disabled={pending}
          onClick={() => {
            if (to === "PUBLISHED") onPublish();
            else onTransition(to);
          }}
        >
          {TRANSITION_ACTIONS[to]}
        </Button>
      ))}
    </div>
  );
}
